import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { RESOURCES } from '@/config/resources';

const LOAD_TIMEOUT_MS = 10_000;

export const ResourceEmbedPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const resource = RESOURCES.find((r) => r.slug === slug);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!resource) return undefined;

    timerRef.current = setTimeout(() => {
      if (loading) {
        setLoading(false);
        setError(true);
      }
    }, LOAD_TIMEOUT_MS);

    return () => clearTimeout(timerRef.current);
  }, [resource, loading]);

  if (!resource) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24">
        <p className="text-muted-foreground text-sm">Resource not found.</p>
        <Link
          to="/resources"
          className="text-sm font-medium text-blue-600 hover:underline"
        >
          Back to Resources
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden">
      <div className="flex items-center gap-3 border-b px-4 py-2">
        <Link
          to="/resources"
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Resources
        </Link>
        <span className="text-muted-foreground text-sm">/</span>
        <span className="text-sm font-medium">{resource.title}</span>
        <a
          href={resource.notionUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline"
        >
          Open in Notion
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      <div className="relative flex-1">
        {loading && !error && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </div>
        )}

        {error ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24">
            <p className="text-muted-foreground text-sm">
              Unable to load this resource inline.
            </p>
            <a
              href={resource.notionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline"
            >
              Open in Notion
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        ) : (
          <iframe
            src={resource.notionUrl}
            title={resource.title}
            className="h-full w-full border-none"
            onLoad={() => {
              clearTimeout(timerRef.current);
              setLoading(false);
            }}
            onError={() => {
              clearTimeout(timerRef.current);
              setLoading(false);
              setError(true);
            }}
          />
        )}
      </div>
    </div>
  );
};
